// src/pages/ChatDocuments/chatPageUi.ts
import type { CSSProperties } from "react";

export interface ChatSidebarDims {
  SIDEBAR_WIDTH: number;
  SIDEBAR_GUTTER: number;    
}

export const makeChatPageUi = ({ SIDEBAR_WIDTH, SIDEBAR_GUTTER }: ChatSidebarDims) => {
  const pageUi = {
    appShell: {
      display: "flex",
      height: "100vh",
      background: "linear-gradient(180deg, rgb(5, 8, 11) 0%, rgb(99, 103, 108) 100%)",
      color: "#0f172a",
      paddingLeft: SIDEBAR_WIDTH + SIDEBAR_GUTTER,
      boxSizing: "border-box",
      minHeight: 0,
      overflow: "hidden",
    } as CSSProperties,

    leftPane: {
      flex: "0 0 250px",
      display: "flex",
      flexDirection: "column",
      gap: 12,
      padding: "20px",
      marginLeft: 0,           // ✅ remove old sidebar compensation
      overflow: "hidden",
    } as CSSProperties,
    

    chatPane: {
      position: "relative",
      flex: "1 1 auto",
      display: "flex",
      flexDirection: "column",
      padding: "16px 20px",
      gap: 6,
      minHeight: 0,
    } as CSSProperties,

    chatHeader: {
      background: "#cccccc",
      border: "1px solid #e2e8f0",
      borderRadius: 14,
      padding: "10px 14px",
      display: "flex",
      alignItems: "center",
      gap: 10,
      boxShadow: "0 1px 2px rgba(15,23,42,.06)",
      marginTop: "-1%",
    } as CSSProperties,

    messages: {
      flex: "1 1 auto",
      overflowY: "auto",
      overflowX: "hidden",
      padding: "14px",
      background: "#cccccc",
      border: "1px solid #e2e8f0",
      borderRadius: 14,
      minHeight: 0,
      scrollbarGutter: "stable both-edges",
      overscrollBehavior: "contain",
      overflowAnchor: "none",
    } as CSSProperties,     

    messagesStack: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-end",
      minHeight: "100%",
    } as CSSProperties,

    aligner: (mine: boolean) =>
      ({
        display: "flex",
        width: "100%",
        minWidth: 0,
        justifyContent: mine ? "flex-end" : "flex-start",
      }) as CSSProperties,

      composer: {
        display: "grid",
        gridTemplateColumns: "auto 1fr auto",
        marginBottom: -5,
        alignItems: "center",              // was "end"
        gap: 8,                            // was 10
        background: "transparent",
        borderRadius: 14,
        padding: 1,  
        overflow: "visible",                 
      } as CSSProperties,

    textarea: {
      width: "94%",
      padding: "6px 12px 6px 8px",
      resize: "none",
      fontSize: 17,
      border: "1px solidrgb(115, 137, 165)",
      borderRadius: 12,
      outline: "none",
      lineHeight: 0.7,
    } as CSSProperties,

    textareaWrap: {
      position: "relative",
      width: "100%",
    } as CSSProperties,

    sendBtn: {
      flex: "0 0 92px",
      padding: "10px 12px",
      background: "#5b21b6",
      color: "#ccc",
      cursor: "pointer",
      border: "none",
      borderRadius: 12,
      marginBottom: 2,
      fontWeight: 600,
    } as CSSProperties,

    attachMenuBase: {
      background: "#1b1b1b",
      color: "#ddd",
      border: "1px solid #2a2a2a",
      borderRadius: 10,
      padding: 6,
      minWidth: 180,
      zIndex: 10000,
      boxShadow: "0 8px 18px rgba(0,0,0,.35)",
    } as CSSProperties,

    msgMenu: (mine: boolean) =>
      ({
        position: "absolute",
        bottom: "100%",
        marginBottom: 8,
        right: mine ? 10 : "auto",
        left: mine ? "auto" : 10,
        background: "#1b1b1b",
        color: "#ddd",
        border: "1px solid #2a2a2a",
        borderRadius: 10,
        padding: 6,
        minWidth: 140,
        zIndex: 50,
        boxShadow: "0 8px 18px rgba(0,0,0,.35)",
      }) as CSSProperties,

      replyChip: {
        display: "flex",
        alignItems: "center",
        gap: 6,                            // was 8
        maxWidth: "100%",
        background: "#eef2ff",             // lighter, subtle
        border: "1px solid #cbd5e1",
        borderLeft: "3px solid #5b21b6",   // small accent to signal “reply”
        borderRadius: 8,
        padding: "3px 8px",                // was "6px 10px"
        marginBottom: 4,                   // was 6
        maxHeight: 24,                     // ensure it stays small
      } as CSSProperties,

    replyLabel: {
      fontSize: 12,
      fontWeight: 700,
      color: "#12161c",
    } as CSSProperties,

    replyText: {
      fontSize: 11,                      // was 12
      color: "#090c0f",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",              // keep it to a single line
      flex: "1 1 auto",
    } as CSSProperties,

    replyClose: {
      background: "transparent",
      border: "none",
      cursor: "pointer",
      fontSize: 14,
      lineHeight: 1,
      color: "#334155",
      padding: 0,
    } as CSSProperties,

    iconBtn: {
      width: 22,                        
      height: 22,                       
      borderRadius: 10,   
      border: "none",      //2px solid rgb(18, 19, 20) alternative
      background: "transparent",
      cursor: "pointer",
      marginBottom: 0,                   
      fontSize: 16,                    
    } as CSSProperties,

    fieldShell: {
      display: "flex",
      alignItems: "center",
      padding: "1px 2px 2px 2px",
      border: "1px solidrgb(127, 140, 157)",
      borderRadius: 12,
      background: "#cccccc",  
      minHeight: 2,
      maxHeight: 140,
      position: "relative",            // ← allow overlay
    } as CSSProperties,

    fieldTextarea: {
      flex: 1,
      resize: "none",
      overflow: "hidden",
      padding: "5px 5px", 
      minHeight: 5,                     // was 15
      maxHeight: 96,                     // was 120
      border: "none",
      outline: "none",
      lineHeight: 1.05,                   // was 0.6 (too compressed)
      font: "inherit",
      fontSize: 15,                      // a bit smaller
      color: "inherit",
      background: "transparent",
        } as CSSProperties,

    fieldEmojiBtn: {
      flex: "0 0 34px",
      width: 34,
      height: 34,
      borderRadius: 8,
      border: "none",
      background: "#cccccc",
      cursor: "pointer",
      fontSize: 22,
    } as CSSProperties,

    sidebarFixed: {
      position: "fixed",
      top: 0,
      left: SIDEBAR_GUTTER,
      width: SIDEBAR_WIDTH,
      height: "100vh",
      overflow: "hidden",
      zIndex: 5,
    } as CSSProperties,

    replyBar: {
      position: "absolute",
      top: 0,                 // ↑ was 1 (or -14); lift more without moving textarea
      left: 10,
      right: 6,
      display: "flex",
      alignItems: "center",
      gap: 6,
      height: 30,
      background: "#eef2ff",
      border: "1px solid #cbd5e1",
      borderLeft: "3px solid #5b21b6",
      borderRadius: 6,
      padding: "0 6px",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      pointerEvents: "auto",
      zIndex: 2,                           // ← sit above the field border
    } as CSSProperties,
  };

  

  return pageUi;
};

export type ChatPageUi = ReturnType<typeof makeChatPageUi>;
